import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiPhone, FiClock, FiTruck, FiShield, FiCheckCircle } = FiIcons;

const CityPages = ({ city }) => {
  const cityData = {
    'Ontario': {
      description: 'Ontario, California is a central hub for logistics and transportation, making CARB compliance critical for local fleets. We provide expert mobile Clean Truck Checks, opacity testing, and emissions compliance services directly to your facility in Ontario. Avoid downtime with our fast, professional service.',
      landmarks: ['Ontario International Airport', 'Victoria Gardens', 'Citizens Business Bank Arena'],
      zipCodes: ['91761', '91762', '91764'],
      population: '175,000+',
      majorEmployers: ['Amazon', 'UPS', 'FedEx']
    },
    'Rancho Cucamonga': {
      description: 'Serving Rancho Cucamonga trucking companies with reliable mobile CARB compliance. Our technicians specialize in diesel emissions testing and fleet maintenance, ensuring your vehicles meet all California state regulations without disrupting your operations.',
      landmarks: ['Victoria Gardens', 'LoanMart Field', 'Cucamonga-Guasti Regional Park'],
      zipCodes: ['91701', '91729', '91730', '91737', '91739'],
      population: '177,000+',
      majorEmployers: ['Coca-Cola', 'Walmart Distribution', 'Sysco Foods']
    },
    'Upland': {
      description: 'Upland fleet operators trust Ramirez Mobile Clean Truck for accurate and timely CARB compliance services. From opacity testing to complete fleet audits, we provide the expertise needed to keep your trucks legal and on the road.',
      landmarks: ['Upland Memorial Park', 'Cooper Regional History Museum', 'San Antonio Heights'],
      zipCodes: ['91784', '91785', '91786'],
      population: '79,000+',
      majorEmployers: ['San Antonio Regional Hospital', 'Upland Unified School District']
    },
    'Fontana': {
      description: 'Fontana is a trucking powerhouse, and we are here to support it. We offer comprehensive mobile CARB compliance testing, diagnostics, and force regens for fleets of all sizes in Fontana and the surrounding industrial areas.',
      landmarks: ['Auto Club Speedway', 'Fontana Park', 'Sierra Lakes Golf Club'],
      zipCodes: ['92335', '92336', '92337'],
      population: '214,000+',
      majorEmployers: ['Amazon', 'Kaiser Permanente', 'Fontana Unified School District']
    },
    'Chino': {
      description: 'Keep your Chino-based fleet compliant with our professional mobile services. We specialize in heavy-duty diesel truck inspections and CARB testing, bringing our expertise directly to your yard.',
      landmarks: ['Prado Regional Park', 'Chino Hills State Park', 'Yanks Air Museum'],
      zipCodes: ['91708', '91710'],
      population: '91,000+',
      majorEmployers: ['Boys Republic', 'California Institution for Men', 'Chino Valley Medical Center']
    },
    'Pomona': {
      description: 'Pomona trucking businesses rely on our efficient and accurate CARB compliance testing. We handle everything from single truck inspections to large fleet management, ensuring you pass Clean Truck Checks with ease.',
      landmarks: ['Fairplex', 'Cal Poly Pomona', 'Pomona Fox Theater'],
      zipCodes: ['91766', '91767', '91768', '91769'],
      population: '153,000+',
      majorEmployers: ['Cal Poly Pomona', 'Pomona Valley Hospital Medical Center', 'LA County Fair']
    },
    'San Bernardino': {
      description: 'As a major logistics center, San Bernardino requires strict adherence to emissions laws. Our mobile team provides rigorous CARB compliance testing and diagnostics to help you avoid fines and keep your freight moving.',
      landmarks: ['San Bernardino National Forest', 'California State University', 'Glen Helen Regional Park'],
      zipCodes: ['92401', '92404', '92405', '92407', '92408', '92410', '92411'],
      population: '222,000+',
      majorEmployers: ['Amazon', 'Stater Bros. Markets', 'County of San Bernardino']
    },
    'Riverside': {
      description: 'We serve Riverside with top-tier mobile CARB compliance solutions. Whether you need an annual inspection or emergency diagnostics, our experienced technicians are ready to assist you on-site.',
      landmarks: ['Mission Inn Hotel & Spa', 'UC Riverside', 'Mount Rubidoux Park'],
      zipCodes: ['92501', '92503', '92504', '92505', '92506', '92507', '92508'],
      population: '331,000+',
      majorEmployers: ['University of California Riverside', 'Riverside County', 'Kaiser Permanente']
    }
  };

  const data = cityData[city] || cityData['Ontario'];

  const services = [
    'Mobile Emissions Testing',
    'CARB Compliance Testing',
    'Smoke Opacity Testing',
    'Fleet Management',
    'Emergency Service',
    'Force Regens'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-blue-900 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1471039497385-b6d6ba609f9c?auto=format&fit=crop&w=1920&q=80" 
            alt="California Highway" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/80"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              CARB Compliance Services in {city}, California
            </motion.h1>
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl mb-8"
            >
              Professional Mobile Diesel Emissions Testing
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-blue-100 mb-8"
            >
              Ramirez Mobile Clean Truck provides comprehensive CARB compliance services 
              throughout {city} and surrounding areas.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a
                href="tel:9092842738"
                className="bg-yellow-500 text-blue-900 font-bold px-8 py-4 rounded-lg hover:bg-yellow-400 transition-colors text-lg flex items-center justify-center space-x-2 shadow-lg"
              >
                <SafeIcon icon={FiPhone} />
                <span>Call (909) 284-2738</span>
              </a>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-blue-900 transition-colors text-lg shadow-lg"
              >
                Get Free Quote
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* City Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                CARB Compliance in {city}, CA
              </h2>
              <h3 className="text-2xl text-gray-600 mb-6">
                Local Expertise for {city} Trucking Companies
              </h3>
              <p className="text-lg text-gray-600 mb-6">{data.description}</p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Population</h4>
                  <p className="text-gray-600">{data.population}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Zip Codes Served</h4>
                  <p className="text-gray-600">{data.zipCodes.join(', ')}</p>
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
                <h4 className="text-xl font-semibold text-gray-800 mb-4">
                  Why {city} Businesses Choose Us
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                    <span className="text-gray-700">Local knowledge of {city} trucking industry</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                    <span className="text-gray-700">Mobile service - we come to you</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                    <span className="text-gray-700">Same-day emergency service available</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                    <span className="text-gray-700">Expert CARB compliance specialists</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg shadow-md border border-gray-100">
              <div className="text-center mb-8">
                <SafeIcon icon={FiMapPin} className="text-4xl text-blue-600 mb-4 mx-auto block" />
                <h3 className="text-2xl font-bold text-gray-800">Serving {city}</h3>
                <p className="text-gray-600">Mobile CARB compliance services</p>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Service Hours</h4>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="text-blue-600" />
                    <span className="text-gray-600">Monday - Saturday: 7AM - 6PM</span>
                  </div>
                  <p className="text-sm text-red-600 mt-1 font-semibold">Emergency service available</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Contact Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiPhone} className="text-blue-600" />
                      <a href="tel:9092842738" className="text-blue-600 hover:underline font-bold">
                        (909) 284-2738
                      </a>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Areas We Cover</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    {data.landmarks.map((landmark, index) => (
                      <div key={index}>• Near {landmark}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services in City */}
      <section className="py-20 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              CARB Compliance Services in {city}
            </h2>
            <h3 className="text-2xl text-gray-600">
              Comprehensive Solutions for {city} Trucking Companies
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center"
              >
                <SafeIcon icon={FiShield} className="text-4xl text-blue-600 mb-4 mx-auto block" />
                <h4 className="text-xl font-semibold text-gray-800 mb-4">{service}</h4>
                <p className="text-gray-600 mb-6">
                  Professional {service.toLowerCase()} services for trucking companies in {city}, CA.
                </p>
                <a
                  href="tel:9092842738"
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-block"
                >
                  Call Now
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Industry */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                {city}'s Trucking & Logistics Industry
              </h2>
              <h3 className="text-2xl text-gray-600">
                Supporting Local Transportation Companies
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h4 className="text-2xl font-semibold text-gray-800 mb-4">
                  Major Employers in {city}
                </h4>
                <div className="space-y-3">
                  {data.majorEmployers.map((employer, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <SafeIcon icon={FiTruck} className="text-blue-600" />
                      <span className="text-gray-700">{employer}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-2xl font-semibold text-gray-800 mb-4">
                  Why CARB Compliance Matters
                </h4>
                <div className="space-y-4 text-gray-600">
                  <p>
                    California's strict emissions standards require regular CARB compliance 
                    testing and maintenance for all diesel trucks operating in {city}.
                  </p>
                  <p>
                    Non-compliance can result in significant fines, vehicle impoundment, 
                    and business disruption. Our mobile services ensure your fleet 
                    stays compliant and operational.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local CTA */}
      <section className="relative py-20 bg-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
           <img 
            src="https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&w=1920&q=80" 
            alt="Highway traffic" 
            className="w-full h-full object-cover"
           />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-4">
            Need CARB Compliance Service in {city}?
          </h2>
          <h3 className="text-2xl mb-8">
            Mobile Service Available Throughout {city}, California
          </h3>
          <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Our expert technicians provide comprehensive CARB compliance services 
            throughout {city} and surrounding areas. Call now for same-day service!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:9092842738"
              className="bg-yellow-500 text-blue-900 font-bold px-8 py-4 rounded-lg hover:bg-yellow-400 transition-colors text-lg"
            >
              Call (909) 284-2738
            </a>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-blue-900 transition-colors text-lg"
            >
              Schedule Service
            </Link>
          </div>
          
          <div className="mt-8 text-blue-200">
            <p>Serving all zip codes in {city}: {data.zipCodes.join(', ')}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityPages;